import React, {FC, memo, useEffect, useState} from "react";
import {message, Space, Table} from "antd";
import {FilterValue, RowSelectionType, SorterResult, TableCurrentDataSource, TablePaginationConfig} from "antd/lib/table/interface";
import {ColumnProps} from "antd/lib/table";
import Page from "../../views/Page/Page";
import {ArrayDataOptions, Maybe, SortOrder, TablesEnum, useGetEmailNotificationsQuery} from "../../generated/graphql";
import Section from "../../views/Section/Section";
import {TableType, useHandleColumns} from "./SentMailsListColumns";
import i18n from "../../services/i18n";
import TableToolBar from "../common/tableTools/TableToolBar";
import {getPagination} from "../../helpers/getPagination";
import {updateFilters} from "../../helpers/updateFilters";
import ActiveFilters from "../common/tableTools/ActiveFilters";
import Roles from "../../../shared/models/roles";
import useHighestRole from "../../helpers/useHighestRole";
import {KnownError, handleError} from "../../errors/handleErrors";
import EmailDetailModal from "./EmailDetailModal/EmailDetailModal";
import {getSortIconTooltipProps, useColumnHeaderDnd} from "../../views/SortIconOnlyHeaderCell/useColumnHeaderDnd";

const checkbox: RowSelectionType = "checkbox";

const variableKeys: {
    [key in keyof TableType]: string;
} = {
    actions: i18n.views.TableColumnTitles.actions(),
    id: i18n.views.TableColumnTitles.id(),
};

const allColumns = (): {
    [key in keyof TableType]: string;
} => {
    const columnName: {
        [key in keyof TableType]: string;
    } = {
        actions: i18n.views.TableColumnTitles.actions(),
        id: i18n.views.TableColumnTitles.id(),
        createDate: i18n.views.TableColumnTitles.sentAt(),
        status: i18n.views.TableColumnTitles.status(),
        receiverName: i18n.views.TableColumnTitles.receiverName(),
        email: i18n.views.TableColumnTitles.email(),
        subject: i18n.views.TableColumnTitles.subject(),
        // templateType: i18n.views.TableColumnTitles.templateType(),
        mailjetTemplateId: i18n.views.TableColumnTitles.mailjetTemplateId(),
        messageHref: i18n.views.TableColumnTitles.messageHref(),
        text: i18n.views.TableColumnTitles.content(),
        templateName: i18n.views.TableColumnTitles.templateName(),
        attachments: i18n.views.TableColumnTitles.attachments(),
        // messageHistory: i18n.views.TableColumnTitles.messageHistory(),
        // messageStatus: i18n.views.TableColumnTitles.messageStatus(),
        bookingId: i18n.views.TableColumnTitles.linkedBooking(),
    };

    return columnName;
};

const mandatoryColumns: Array<keyof TableType> = ["actions", "email", "subject"];
const pinnedColumns: Array<keyof TableType> = ["actions"];

const standarHiddenColumns: Array<keyof TableType> = ["id", "mailjetTemplateId", "messageHref"];

const createLink = "";
const editLink = "";
const detailsLink = "";

type SentMailsListProps = {
    toolBarButtons?: Array<"columnSelector" | "pdf" | "eMail" | "bulkDelete" | "checkList">;
    tableTitle?: string;
    hiddenColumns?: Array<keyof TableType>;
    defaultPageSize?: number;
    tableData?: {
        dataSource: Array<TableType>;
        total: number;
        loading: boolean;
    };
    tablesEnum?: TablesEnum;
    options: Maybe<ArrayDataOptions>;
    setOptions: (options: ArrayDataOptions) => void;
    hidePagination?: boolean;
    hideSortFilter?: boolean;
    standardColumns: Array<keyof TableType>;
    // mandatoryColumns?: Array<string>;
    totalResults: number;
    setTotalResults: (totalResults: number) => void;
    refetch?: () => void;
};

const SentMailsList: FC<SentMailsListProps> = ({
    toolBarButtons = ["columnSelector", "pdf", "eMail", "bulkDelete"],
    defaultPageSize = 15,
    tableTitle = i18n.containers.notifications.Notifications.table.title(),
    tableData,
    tablesEnum = TablesEnum.NotificationsSent,
    options,
    setOptions,
    hidePagination = false,
    hideSortFilter = false,
    standardColumns,
    totalResults,
    setTotalResults,
    refetch: externalRefetch,
}) => {
    const highestRole = useHighestRole();
    const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
    // GET DATA FOR LIST --------------------------------------------------------------------------------------------------------------------

    const {data, loading, error, refetch} = useGetEmailNotificationsQuery({
        skip: Boolean(tableData),
        variables: {options},
        fetchPolicy: "cache-and-network",
        partialRefetch: true,
    });

    useEffect(() => {
        totalResults !== data?.emailNotifications?.total && setTotalResults(data?.emailNotifications?.total ?? 0);
    }, [data, setTotalResults, totalResults]);

    if (error) {
        console.log(`Fehler: ${error.name} Achtung, irgendwas ist schief gelaufen: ${error.message}`);
        console.log(error);

        const knownErrors: Array<KnownError> = [
            {
                key: "Too Many Requests",
                title: "Zu viele Anfragen",
                msg: "Unser Versanddienstleiter erlaubt uns nur eine gewisse Anzahl an Anfragen pro Minute. Du kannst es in wenigen Minuten erneut versuchen.",
            },
            {
                key: "default",
                title: "Fehler beim Abrufen der Daten",
                msg: "Bitte versuche es in ein paar Minuten erneut. Sollte das Problem weiterhin bestehen, kontaktiere bitte den Support.",
            },
        ];

        handleError("transferOrCopyAttendeeToAnotherCoursePlacereservation", error, "message", knownErrors);
    }

    const dataSource: Array<TableType> = data?.emailNotifications?.items ?? [];
    const total: number = data?.emailNotifications?.total ?? 0;

    // const actualTableData = {dataSource, total, loading};
    const actualTableData = tableData ? tableData : {dataSource, total, loading};

    // HANDLE COLUMNS ---------------------------------------------------------

    const [columnsToShow, setColumnsToShow] = useState<Array<Extract<keyof TableType, string>>>([]);

    const columns: Array<ColumnProps<TableType>> = useHandleColumns(columnsToShow, options, setOptions, options?.filter, hideSortFilter, setSelectedNotificationId);

    // ------------------------------ Spalten-Header Drag-and-Drop ------------------------------
    // Verdrahtet den generischen Header-DnD-Kern. `columns` (inkl. fixed + Post-Moves)
    // ist der unveränderte Input; `dndColumns`/`components` ersetzen die bisherigen
    // `columns`/`getListTableComponents()`-Props an <Table>. `wrapWithDnd` liefert den
    // Table-lokalen DndProvider.
    const {components: dndComponents, dndColumns, wrapWithDnd} = useColumnHeaderDnd({
        columns,
        columnsToShow,
        // Setter-Adapter: Der Hook arbeitet generisch mit Array<string>, der lokale
        // State ist enger typisiert (Array<Extract<keyof TableType, string>>). Gleiches
        // Cast-Muster wie bei der TableToolBar-Verdrahtung (setSelectedColumns).
        setColumnsToShow: (next: Array<string>) => setColumnsToShow(next as Array<Extract<keyof TableType, string>>),
        tablesEnum: tablesEnum,
        mandatoryColumns: mandatoryColumns as Array<string>,
        pinnedColumns: pinnedColumns as Array<string>,
    });

    // ON CHANGE ------------------------------------------------------------------------------------------------------------------------

    const onChange: (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TableType> | Array<SorterResult<TableType>>,
        extra?: TableCurrentDataSource<TableType>,
    ) => void = (pagination, filters, sorter, extra) => {
        const updatedFilters = updateFilters({
            filters,
            options,
            table: "notification",
        });

        const sortColumn = sorter.field;
        const sortOrder: Maybe<SortOrder> = sorter.order === "ascend" ? SortOrder.Ascending : sorter.order === "descend" ? SortOrder.Descending : undefined;

        const updatedOptions = {
            ...options,
            filter: updatedFilters,
            limit: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).limit,
            offset: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).offset,
            sortColumn,
            sortOrder,
            // sortColumn: getSortOrder(sorter).sortColumn,
            // sortOrder: getSortOrder(sorter).sortOrder,
        };

        setOptions(updatedOptions);
    };

    const renderContent = () => {
        return (
            <>
            <Section
                title={tableTitle}
                secondLineLeft={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignContent: "center",
                            padding: "2px 0px",
                        }}
                    >
                        <TableToolBar
                            buttons={toolBarButtons}
                            columns={columns}
                            allColumns={allColumns()}
                            hiddenColumns={highestRole === Roles.Superuser ? [] : standarHiddenColumns}
                            variableKeys={variableKeys}
                            columnsToShow={columnsToShow}
                            setSelectedColumns={(e: Array<keyof TableType>) => setColumnsToShow(e)}
                            dataSource={actualTableData.dataSource}
                            tablesEnum={tablesEnum}
                            defaultColumns={standardColumns as Array<string>}
                            mandatoryColumns={mandatoryColumns as Array<string>}
                            pinnedColumns={pinnedColumns as Array<string>}
                            activeFilters={<ActiveFilters options={options} setOptions={setOptions} refetch={externalRefetch ?? refetch} style={{flex: 1}} />}
                        />
                    </div>
                }
                content={wrapWithDnd(
                    <Table
                        // className={css.root}
                        showSorterTooltip={getSortIconTooltipProps()}
                        size="small"
                        bordered
                        components={dndComponents}
                        columns={dndColumns}
                        // dataSource={dataSource}
                        dataSource={actualTableData.dataSource}
                        rowKey="id"
                        onRow={(record) => ({
                            onDoubleClick: () => setSelectedNotificationId(record.id),
                            style: {cursor: "pointer"},
                        })}
                        // loading={loading}
                        loading={actualTableData.loading}
                        // rowSelection={rowSelection.result}
                        sticky={dataSource.length > 0}
                        scroll={
                            dataSource.length
                                ? {
                                      x: `max-content`,
                                      y: "calc(100vh - 260px)",
                                  }
                                : {x: "auto"}
                        }
                        onChange={onChange}
                        // pagination={{
                        //     showSizeChanger: true,
                        //     pageSizeOptions: ["10", "15", "20", "25"],
                        //     defaultPageSize,
                        //     total: actualTableData.total,
                        // }}
                        pagination={
                            hidePagination
                                ? false
                                : {
                                      current: options?.offset && options?.limit ? options.offset / options.limit + 1 : 1,
                                      pageSize: options?.limit ?? defaultPageSize,
                                      showSizeChanger: true,
                                      pageSizeOptions: ["10", "15", "20", "25"],
                                      defaultPageSize,
                                      total: actualTableData.total,
                                  }
                        }
                        footer={(currentPageData) => `${i18n.containers.notifications.Notifications.table.totalNumber()} ${actualTableData.total}`}
                    />,
                    )}
            />
            <EmailDetailModal
                notificationId={selectedNotificationId}
                onClose={() => setSelectedNotificationId(null)}
            />
            </>
        );
    };

    return <Page data-testid="SentMails" content={renderContent()} />;
};

export default memo(SentMailsList);
